import { useEffect, useRef } from 'react';

const stats = [
  { value: '8', label: 'Guest Rooms' },
  { value: '1940s', label: 'Art Déco Villa' },
  { value: '#1', label: 'B&B in Matagalpa' },
  { value: '5★', label: 'TripAdvisor' },
];

const propertyExtras = [
  '2 additional fully equipped rooms',
  '1 additional full bathroom',
  '2 half bathrooms',
  '3 storage rooms',
  'Parking for 10 vehicles',
  '2013 Toyota Hilux (16 passengers)',
];

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('is-visible');
      }),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-20 md:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Sale headline */}
        <div className="text-center mb-14 reveal">
          <div className="inline-flex items-center gap-2 bg-gold/10 text-gold rounded-full px-4 py-1.5 mb-4">
            <i className="ri-building-2-line text-sm w-4 h-4 flex items-center justify-center" />
            <span className="font-sans text-xs tracking-widest uppercase">About the Property</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-forest-dark leading-tight mb-4">
            A Rare Turnkey Business Opportunity<br />in the Heart of Matagalpa, in the Nicaraguan Highlands
          </h2>
          <p className="font-sans text-base text-gray-500 max-w-3xl mx-auto">
            Montebrisa is a fully operational, award-winning boutique Bed &amp; Breakfast housed in a lovingly restored 1940s Art Déco villa. The sale includes the complete running business — licenses, infrastructure, furnishings, vehicles, and international booking platform.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
          {/* Left Images */}
          <div className="w-full lg:w-[46%] reveal">
            <div className="relative rounded-2xl overflow-hidden" style={{ height: '480px' }}>
              <img
                src="https://montebrisa.com/wp-content/uploads/slider/cache/ad3d497a3ec068be6c43dd96f48e98da/piedra-top1.jpg"
                alt="Montebrisa property grounds"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3">
                <p className="font-serif text-forest text-base font-medium">Urban Oasis</p>
                <p className="font-sans text-xs text-gray-500 mt-0.5">3 blocks from Matagalpa Cathedral &amp; Parque Morazán</p>
              </div>
            </div>
            <div className="mt-4 rounded-xl overflow-hidden h-44">
              <img
                src="https://montebrisa.com/wp-content/uploads/slider/cache/e9190c7b35ff443a2cdc072155b98214/top-asientos.jpg"
                alt="Montebrisa outdoor seating area"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>

          {/* Right Content */}
          <div className="w-full lg:w-[54%] reveal" style={{ transitionDelay: '0.15s' }}>
            <h3 className="font-serif text-2xl md:text-3xl text-forest-dark leading-snug mb-4">
              Despite its proximity to the centre of town, it feels as if you are in the countryside once you enter its walls.
            </h3>
            <p className="font-sans text-sm text-gray-600 leading-relaxed mb-5">
              Located in an urban oasis surrounded by lush tropical gardens with the Apante Natural Reserve and El Calvario Park as backdrop — just 3 blocks from Matagalpa&apos;s Cathedral and Central Park, and within walking distance of cafés, restaurants, and shops.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-3 mb-7">
              {stats.map(s => (
                <div key={s.label} className="text-center p-3 bg-white rounded-xl border border-cream-dark">
                  <p className="font-serif text-xl text-gold font-semibold">{s.value}</p>
                  <p className="font-sans text-xs text-gray-500 mt-1 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>

            {/* What's included */}
            <div className="bg-white rounded-xl border border-cream-dark p-5 mb-5">
              <h4 className="font-sans text-xs tracking-widest uppercase text-gray-400 mb-3">Full Turnkey Sale Includes</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  'Operating B&B licenses & permits',
                  'Montebrisa.com + e-commerce platform',
                  'Custom Nicaraguan wood furnishings',
                  'All cookware, linen & equipment',
                  '2013 Toyota Hilux (16 passengers)',
                  'All booking platform accounts',
                  'Active TripAdvisor profile & reviews',
                  'GreenLeaders certification',
                ].map(item => (
                  <div key={item} className="flex items-start gap-2">
                    <i className="ri-check-line text-gold text-sm w-4 h-4 flex items-center justify-center mt-0.5 shrink-0" />
                    <span className="font-sans text-xs text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional spaces */}
            <div className="bg-forest-50 rounded-xl p-5">
              <h4 className="font-sans text-xs tracking-widest uppercase text-forest mb-3">Additional Property Spaces</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {propertyExtras.map(item => (
                  <div key={item} className="flex items-start gap-2">
                    <i className="ri-home-4-line text-forest text-xs w-4 h-4 flex items-center justify-center mt-0.5 shrink-0" />
                    <span className="font-sans text-xs text-forest-dark">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
